import { useEffect, useState } from 'react';

/**
 * Report whether an element is on screen.
 *
 * Takes the element, not a ref holding it. A ref's `current` is still null
 * while React renders the element that will fill it, so an effect keyed on
 * `ref.current` reads null on the render that mounts the node, sees no change
 * in its dependencies, and never observes anything. An element held in state
 * re-renders the caller at the point it is attached, which is exactly when the
 * observer can start.
 *
 * @param {Element|null} element the element to observe
 * @param {string} rootMargin how many pixels beyond the element count as seen
 * @returns {boolean} true while the element is on screen
 */
const useOnScreen = (element, rootMargin = '0px') => {
  const [isVisible, setIfVisible] = useState(false);

  useEffect(() => {
    if (!element) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setIfVisible(entry.isIntersecting),
      { rootMargin },
    );
    observer.observe(element);

    return () => observer.disconnect();
  }, [element, rootMargin]);

  return isVisible;
};

export default useOnScreen;
